import { ItemTeaser, Layout, Modal } from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import { IconFooterCMS, IconFooterItemCMS } from '@cp-shared-15/apis';
import { Override } from '@cp-shared-15/common-utilities';
import { useHistory } from 'react-router-dom';
import { allowedFractions } from 'components/contracts';

export type IconFooterItem = Override<
    IconFooterItemCMS,
    {
        text: string | React.ReactNode;
        modalContent?: string | React.ReactNode;
    }
>;

export type IconFooterProps = Override<
    IconFooterCMS,
    {
        items: IconFooterItem[];
        trackingFunction?: (title: string, currentPageName?: string) => void;
        currentPageName?: string;
        trackingFunctions?: { [trackingId: string]: () => void };
        headingLevel?: 5 | 2 | 3 | 4 | 6 | undefined;
    }
>;

const isExternalLink = (url?: string): boolean => !!url && /^(https?:\/\/|mailto:|tel:)/.test(url);

const isNormalLink = (normalLink?: boolean, link?: string): boolean => normalLink || isExternalLink(link);

export const IconFooter: React.FC<IconFooterProps> = ({
    items,
    title,
    trackingFunction,
    currentPageName,
    trackingFunctions,
    headingLevel = 5,
}) => {
    const [withModal, setWithModal] = useState(false);
    const [modalContent, setModalContent] = useState<string | React.ReactNode>('');
    const history = useHistory();

    return (
        <Layout>
            {title && (
                <Layout.Item className="u-text-center" default="1/1">
                    <h2>{title}</h2>
                </Layout.Item>
            )}
            <Layout.Item default="1/1">
                <Layout className="row">
                    {items.map((item: IconFooterItem) => {
                        const isExternal = isNormalLink(item.normalLink, item.linkUrl);
                        const isRelativePath = !!item.linkUrl && item.linkUrl.startsWith('/');

                        return (
                            <Layout.Item
                                key={item.title}
                                default={items.length <= 6 ? (`1/${items.length}` as allowedFractions) : '1/12'}
                                m="1/1"
                            >
                                <ItemTeaser
                                    linkProps={{
                                        element: 'a',
                                        href: item.linkUrl,
                                        title: item.title,
                                        target: item.target,
                                        onClick: (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): void => {
                                            if (item.trackItem) {
                                                if (item.trackingId && trackingFunctions?.[item.trackingId]) {
                                                    trackingFunctions[item.trackingId]();
                                                } else if (trackingFunction) {
                                                    trackingFunction(item.title, currentPageName);
                                                }
                                            }

                                            if (item.modalContent) {
                                                event.preventDefault();
                                                setWithModal(true);
                                                setModalContent(item.modalContent);
                                                return;
                                            }

                                            if (isRelativePath && !isExternal) {
                                                if (!event.metaKey && !event.ctrlKey) {
                                                    event.preventDefault();
                                                    history.push(item.linkUrl!);
                                                }
                                            }
                                        },
                                        ...(item.ariaLabel ? { ariaLabel: item.ariaLabel } : {}),
                                    }}
                                    icon={item.icon}
                                    title={item.title}
                                    linkLabel={item.linkText}
                                    className={item.className}
                                    iconClassName={item.iconClassName}
                                    testId={item.testId}
                                    small={item.small}
                                    inline={item.inline !== undefined ? item.inline : true}
                                    inlineSmallScreen={item.inlineSmallScreen}
                                    block={item.block}
                                    blockSmallScreen={item.blockSmallScreen}
                                    headingLevel={headingLevel}
                                >
                                    {item.text}
                                </ItemTeaser>

                                {withModal && (
                                    <Modal
                                        title={item.title}
                                        shown={withModal}
                                        testId={`icon-footer-item-modal-${item.title}`}
                                        onClose={(): void => setWithModal(false)}
                                    >
                                        {modalContent}
                                    </Modal>
                                )}
                            </Layout.Item>
                        );
                    })}
                </Layout>
            </Layout.Item>
        </Layout>
    );
};
